/**
 * zod schemas shared between Mini App (client) and Worker (server).
 *
 * Source of truth for runtime API types. DB types come from Drizzle.
 */
import { z } from 'zod';

// ---------------------------------------------------------------------------
// Primitives
// ---------------------------------------------------------------------------
/**
 * Stable-id string. We use prefixed slugs (`cty_belgrade`, `rt_bg_ns`, `usr_<uuid>`)
 * so the min length is intentionally permissive (covers short city ids like `cty_nis`).
 */
export const uuidLikeSchema = z.string().min(4).max(64);
export const telegramIdSchema = z.number().int().positive();
export const isoDateSchema = z.string().datetime({ offset: true });
export const isoDateOnlySchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

// ---------------------------------------------------------------------------
// Auth
// ---------------------------------------------------------------------------
export const telegramAuthRequestSchema = z.object({
  initData: z.string().min(1).max(8192),
  /** Optional referral code from URL/start_param (e.g. ?ref=AB123). */
  referralCode: z.string().max(32).optional(),
});
export type TelegramAuthRequest = z.infer<typeof telegramAuthRequestSchema>;

export const telegramUserSchema = z.object({
  id: telegramIdSchema,
  is_bot: z.boolean().optional(),
  first_name: z.string(),
  last_name: z.string().optional(),
  username: z.string().optional(),
  language_code: z.string().optional(),
  is_premium: z.boolean().optional(),
  added_to_attachment_menu: z.boolean().optional(),
  allows_write_to_pm: z.boolean().optional(),
  photo_url: z.string().url().optional(),
});
export type TelegramUser = z.infer<typeof telegramUserSchema>;

// ---------------------------------------------------------------------------
// User / Profile
// ---------------------------------------------------------------------------
export const vibesSchema = z.object({
  music: z.boolean().nullable(),
  smoking: z.boolean().nullable(),
  pets: z.boolean().nullable(),
  luggage: z.enum(['small', 'medium', 'large']).nullable(),
  womenOnly: z.boolean().nullable(),
  /** 0=quiet, 1=sometimes, 2=chatty, null=no preference. */
  chatLevel: z.union([z.literal(0), z.literal(1), z.literal(2), z.null()]),
});
export type Vibes = z.infer<typeof vibesSchema>;

export const publicUserSchema = z.object({
  id: uuidLikeSchema,
  /** Only non-null if the viewer has an approved booking AND user has telegram_visible = true. */
  telegramUsername: z.string().nullable(),
  /** Only non-null if the viewer has an approved booking AND user has phone_visible = true. */
  phone: z.string().nullable(),
  displayName: z.string(),
  avatarUrl: z.string().url().nullable(),
  /** Only non-null if user has bio_visible = true. */
  bio: z.string().nullable(),
  isDriver: z.boolean(),
  trustScore: z.number(),
  /** Behaviour-derived reliability score, 0-100. See reliability.ts. */
  reliability: z.number(),
  ridesCompleted: z.number().int(),
  ratingAvg: z.number().nullable(),
  ratingCount: z.number().int(),
  isPremium: z.boolean(),
  /** Driver car-comfort flags (shown on the ride detail when true). */
  carAc: z.boolean(),
  carCharger: z.boolean(),
  carMiddleSeat: z.boolean(),
  badges: z.array(z.string()),
  /** Driver tier label key, e.g. `tier.regular`. 'tier.newcomer' for non-drivers. */
  tierLabelKey: z.string(),
  /** Numeric level 0-4 for visual hierarchy. */
  tierLevel: z.number().int().min(0).max(4),
  /** ISO date the user joined (publicly shown). */
  memberSince: isoDateSchema,
  /** Travel vibes — user-level preference defaults. Nullable fields = not set. */
  vibes: vibesSchema,
});
export type PublicUser = z.infer<typeof publicUserSchema>;

export const SUPPORTED_LOCALES = ['en', 'sr', 'bs', 'hr'] as const;
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

export const localeSchema = z.enum(SUPPORTED_LOCALES);

export const meSchema = publicUserSchema.extend({
  locale: localeSchema,
  countryCode: z.string(),
  carMake: z.string().nullable(),
  carModel: z.string().nullable(),
  carColor: z.string().nullable(),
  carPlate: z.string().nullable(),
  onboardedAt: z.string().nullable(),
  rulesAcceptedAt: z.string().nullable(),
  /** /me always sees its own phone, even if hidden to others. */
  phoneVisible: z.boolean(),
  telegramVisible: z.boolean(),
  bioVisible: z.boolean(),
  emergencyContactName: z.string().nullable(),
  emergencyContactPhone: z.string().nullable(),
  referralCode: z.string().nullable(),
  /** Computed stats. */
  referralsCount: z.number().int(),
  referralsRewarded: z.number().int(),
});
export type Me = z.infer<typeof meSchema>;

/** E.164-ish: + then 6-15 digits. Loose enough for international, strict enough to reject garbage. */
const phoneSchema = z
  .string()
  .regex(/^\+\d{6,15}$/, "Phone must be in international format, e.g. +381601234567");

export const updateProfileRequestSchema = z.object({
  displayName: z.string().min(1).max(60).optional(),
  bio: z.string().max(280).optional(),
  isDriver: z.boolean().optional(),
  carMake: z.string().max(40).optional(),
  carModel: z.string().max(40).optional(),
  carColor: z.string().max(20).optional(),
  carPlate: z.string().max(15).optional(),
  carAc: z.boolean().optional(),
  carCharger: z.boolean().optional(),
  carMiddleSeat: z.boolean().optional(),
  locale: localeSchema.optional(),
  phone: phoneSchema.or(z.literal('')).optional(),
  phoneVisible: z.boolean().optional(),
  telegramVisible: z.boolean().optional(),
  bioVisible: z.boolean().optional(),
  emergencyContactName: z.string().max(60).or(z.literal('')).optional(),
  emergencyContactPhone: phoneSchema.or(z.literal('')).optional(),
  vibes: vibesSchema.optional(),
  acceptRules: z.boolean().optional(),
  acceptDriverTerms: z.boolean().optional(),
});

// ---------------------------------------------------------------------------
// Ride status updates (driver → all approved passengers)
// ---------------------------------------------------------------------------
export const RIDE_STATUS_UPDATE_CODES = [
  'leaving_soon',
  'at_pickup',
  'departed',
  'arrived',
] as const;
export const rideStatusUpdateCodeSchema = z.enum(RIDE_STATUS_UPDATE_CODES);
export type RideStatusUpdateCode = z.infer<typeof rideStatusUpdateCodeSchema>;

export const postRideStatusRequestSchema = z.object({
  status: rideStatusUpdateCodeSchema,
  note: z.string().max(200).optional(),
});
export type PostRideStatusRequest = z.infer<typeof postRideStatusRequestSchema>;

export const rideStatusUpdateDtoSchema = z.object({
  id: uuidLikeSchema,
  rideId: uuidLikeSchema,
  status: rideStatusUpdateCodeSchema,
  note: z.string().nullable(),
  createdAt: isoDateSchema,
});
export type RideStatusUpdateDto = z.infer<typeof rideStatusUpdateDtoSchema>;

// ---------------------------------------------------------------------------
// Safety alert — POST /api/v1/bookings/:id/safety-alert
// ---------------------------------------------------------------------------
export const safetyAlertRequestSchema = z.object({
  /** Optional free-text — user describes what's happening if they can. */
  note: z.string().max(500).optional(),
  /** If true, the server contacts the user's emergency contact. */
  notifyEmergencyContact: z.boolean().default(true),
});
export type SafetyAlertRequest = z.infer<typeof safetyAlertRequestSchema>;
export type UpdateProfileRequest = z.infer<typeof updateProfileRequestSchema>;

// ---------------------------------------------------------------------------
// Geography
// ---------------------------------------------------------------------------
export const citySchema = z.object({
  id: uuidLikeSchema,
  slug: z.string(),
  nameEn: z.string(),
  nameSr: z.string(),
  countryCode: z.string(),
  lat: z.number(),
  lng: z.number(),
  // Search-ranking signal only (always sent by the API via toCityDto).
  population: z.number().int(),
});
export type CityDto = z.infer<typeof citySchema>;

export const routeSchema = z.object({
  id: uuidLikeSchema,
  originCityId: uuidLikeSchema,
  destinationCityId: uuidLikeSchema,
  origin: citySchema,
  destination: citySchema,
  distanceKm: z.number(),
  referenceCost: z.number(),
  currency: z.string(),
  channelHandle: z.string().nullable(),
});
export type RouteDto = z.infer<typeof routeSchema>;

// ---------------------------------------------------------------------------
// Rides
// ---------------------------------------------------------------------------
export const rideStatusSchema = z.enum([
  'draft',
  'under_review',
  'published',
  'full',
  'departed',
  'completed',
  'auto_completed',
  'cancelled_by_driver',
  'rejected',
]);

export const ridePreferencesSchema = z.object({
  smokingAllowed: z.boolean(),
  musicAllowed: z.boolean(),
  petsAllowed: z.boolean(),
  luggageMax: z.enum(['small', 'medium', 'large']),
  womenOnly: z.boolean(),
});

export const publishRideRequestSchema = z
  .object({
    /** Predefined route. Provide this OR (originCityId + destinationCityId). */
    routeId: uuidLikeSchema.optional(),
    /** Custom route endpoints — the server find-or-creates the route. */
    originCityId: uuidLikeSchema.optional(),
    destinationCityId: uuidLikeSchema.optional(),
    departureAt: isoDateSchema,
    flexibilityMinutes: z.number().int().min(0).max(60),
    seatsTotal: z.number().int().min(1).max(4),
    proposedCost: z.number().positive().max(1_000_000),
    approvalMode: z.enum(['auto', 'manual']),
    preferences: ridePreferencesSchema,
    pickupNotes: z.string().max(280).optional(),
    /** Visual pickup point (Phase B) — short label + coarse coords (~3dp). */
    pickupTitle: z.string().max(80).optional(),
    pickupLat: z.number().min(-90).max(90).optional(),
    pickupLng: z.number().min(-180).max(180).optional(),
    /** Ordered intermediate city ids the driver passes through (optional). */
    viaCityIds: z.array(uuidLikeSchema).max(8).optional(),
    /** Let passengers book part of the trip (only meaningful with via stops). */
    allowPartial: z.boolean().optional(),
  })
  .refine((v) => Boolean(v.routeId) || Boolean(v.originCityId && v.destinationCityId), {
    message: 'Provide routeId or both originCityId and destinationCityId',
  })
  .refine((v) => !(v.originCityId && v.destinationCityId) || v.originCityId !== v.destinationCityId, {
    message: 'Origin and destination must differ',
  });
export type PublishRideRequest = z.infer<typeof publishRideRequestSchema>;

/**
 * Edit a published ride. Every field optional — the server applies only what's
 * present. Fields that a passenger booked on (itinerary/price/time) are frozen
 * once the ride has active bookings; see RIDE_FIELDS_LOCKED_WHEN_BOOKED.
 */
export const editRideRequestSchema = z
  .object({
    departureAt: isoDateSchema.optional(),
    flexibilityMinutes: z.number().int().min(0).max(60).optional(),
    seatsTotal: z.number().int().min(1).max(4).optional(),
    proposedCost: z.number().positive().max(1_000_000).optional(),
    approvalMode: z.enum(['auto', 'manual']).optional(),
    preferences: ridePreferencesSchema.optional(),
    pickupNotes: z.string().max(280).nullable().optional(),
    pickupTitle: z.string().max(80).nullable().optional(),
    pickupLat: z.number().min(-90).max(90).nullable().optional(),
    pickupLng: z.number().min(-180).max(180).nullable().optional(),
    routeId: uuidLikeSchema.optional(),
    originCityId: uuidLikeSchema.optional(),
    destinationCityId: uuidLikeSchema.optional(),
    viaCityIds: z.array(uuidLikeSchema).max(8).optional(),
    allowPartial: z.boolean().optional(),
  })
  .refine((v) => Object.values(v).some((x) => x !== undefined), {
    message: 'No fields to update',
  });
export type EditRideRequest = z.infer<typeof editRideRequestSchema>;

/**
 * Ride fields frozen once the ride has active (pending/approved) bookings —
 * these are the terms a passenger agreed to. Changing them requires cancel +
 * repost. (Also closes the AA bypass: price can only be edited while unbooked,
 * where the edit re-runs evaluatePublishRide.)
 */
export const RIDE_FIELDS_LOCKED_WHEN_BOOKED = [
  'departureAt',
  'flexibilityMinutes',
  'proposedCost',
  'routeId',
  'originCityId',
  'destinationCityId',
  'viaCityIds',
] as const;

/** True if `field` cannot be edited given the ride's booking state. */
export function rideEditFieldLocked(field: string, hasActiveBookings: boolean): boolean {
  return hasActiveBookings && (RIDE_FIELDS_LOCKED_WHEN_BOOKED as readonly string[]).includes(field);
}

// ---------------------------------------------------------------------------
// Recurring ride templates
// ---------------------------------------------------------------------------
export const weekdayMaskSchema = z.number().int().min(1).max(127);

export const createRideTemplateRequestSchema = z.object({
  routeId: uuidLikeSchema,
  weekdayMask: weekdayMaskSchema,
  /** HH:MM string (24h). */
  departureTime: z.string().regex(/^\d{2}:\d{2}$/),
  flexibilityMinutes: z.number().int().min(0).max(60),
  seatsTotal: z.number().int().min(1).max(4),
  proposedCost: z.number().positive().max(1_000_000),
  approvalMode: z.enum(['auto', 'manual']),
  preferences: ridePreferencesSchema,
  pickupNotes: z.string().max(280).optional(),
  startsOn: isoDateOnlySchema.optional(),
  endsOn: isoDateOnlySchema.optional(),
  /** Ordered intermediate city ids the driver passes through (optional). */
  viaCityIds: z.array(uuidLikeSchema).max(8).optional(),
});
export type CreateRideTemplateRequest = z.infer<typeof createRideTemplateRequestSchema>;

export const rideTemplateDtoSchema = z.object({
  id: uuidLikeSchema,
  routeId: uuidLikeSchema,
  route: routeSchema,
  weekdayMask: z.number().int(),
  /** HH:MM */
  departureTime: z.string(),
  flexibilityMinutes: z.number().int(),
  seatsTotal: z.number().int(),
  proposedCost: z.number(),
  currency: z.string(),
  approvalMode: z.enum(['auto', 'manual']),
  preferences: ridePreferencesSchema,
  pickupNotes: z.string().nullable(),
  isActive: z.boolean(),
  horizonDays: z.number().int(),
  startsOn: isoDateOnlySchema.nullable(),
  endsOn: isoDateOnlySchema.nullable(),
  /** Computed: human-readable e.g. "Mon, Tue, Thu" — server localizes lazily. */
  weekdaySummary: z.string(),
  /** Computed: number of materialized future rides not yet departed. */
  upcomingCount: z.number().int(),
  /** Hydrated intermediate cities the driver passes through, in order. */
  viaCities: z.array(citySchema),
});
export type RideTemplateDto = z.infer<typeof rideTemplateDtoSchema>;

export const rideDtoSchema = z.object({
  id: uuidLikeSchema,
  routeId: uuidLikeSchema,
  route: routeSchema,
  driver: publicUserSchema,
  departureAt: isoDateSchema,
  flexibilityMinutes: z.number().int(),
  seatsTotal: z.number().int(),
  seatsRemaining: z.number().int(),
  /** True if the ride has ≥1 pending/approved booking. Drives edit-locking. */
  hasActiveBookings: z.boolean(),
  proposedCost: z.number(),
  referenceCost: z.number(),
  currency: z.string(),
  approvalMode: z.enum(['auto', 'manual']),
  preferences: ridePreferencesSchema,
  pickupNotes: z.string().nullable(),
  /** Short pickup label — shown to everyone (no precise location leaked). */
  pickupTitle: z.string().nullable(),
  /** Precise pickup coords — only present once the viewer has an approved
   * booking (or is the driver). Null otherwise (privacy gate). */
  pickupLat: z.number().nullable(),
  pickupLng: z.number().nullable(),
  status: rideStatusSchema,
  driverConfirmedAt: isoDateSchema.nullable(),
  flaggedReason: z.string().nullable(),
  /** Hydrated intermediate cities the driver passes through, in order. */
  viaCities: z.array(citySchema),
  /** Driver allows booking part of the trip (A→stop, stop→B). */
  allowPartial: z.boolean(),
  /** Set on sub-path search results: the searched A→B segment + its fare, so the
   * result card shows the segment price (not the full ride) and the ride screen
   * can pre-select these stops. Null for exact matches / non-search contexts. */
  matchedSegment: z
    .object({ fromCityId: uuidLikeSchema, toCityId: uuidLikeSchema, price: z.number() })
    .nullable(),
});
export type RideDto = z.infer<typeof rideDtoSchema>;

export const ridesSearchRequestSchema = z.object({
  originCityId: uuidLikeSchema,
  destinationCityId: uuidLikeSchema,
  date: isoDateOnlySchema,
  timeFrom: z.string().regex(/^\d{2}:\d{2}$/).optional(),
  timeTo: z.string().regex(/^\d{2}:\d{2}$/).optional(),
  maxPrice: z.coerce.number().positive().optional(),
  minRating: z.coerce.number().min(1).max(5).optional(),
  womenOnly: z.coerce.boolean().optional(),
  sort: z.enum(['departure', 'price', 'rating']).optional(),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(50).optional(),
});
export type RidesSearchRequest = z.infer<typeof ridesSearchRequestSchema>;

// ---------------------------------------------------------------------------
// Bookings
// ---------------------------------------------------------------------------
export const bookingStatusSchema = z.enum([
  'pending',
  'approved',
  'declined',
  'cancelled_by_passenger',
  'cancelled_by_driver',
  'completed',
  'rated',
  'auto_closed',
  'no_show',
  'under_review',
]);

export const createBookingRequestSchema = z.object({
  seats: z.number().int().min(1).max(4),
  messageToDriver: z.string().max(200).optional(),
  luggage: z.enum(['small', 'medium', 'large']).optional(),
  // Named co-passengers (group booking). At most seats − 1 names; enforced
  // against `seats` in the booking service. Each name kept short.
  companions: z.array(z.string().trim().min(1).max(40)).max(3).optional(),
  // Partial trip: board/alight at intermediate stops. Both set together, must
  // be stops on the ride's path, and the ride must allow partial booking.
  // Omitted (or equal to origin/destination) = full trip.
  fromCityId: uuidLikeSchema.optional(),
  toCityId: uuidLikeSchema.optional(),
});
export type CreateBookingRequest = z.infer<typeof createBookingRequestSchema>;

export const bookingRideSummarySchema = z.object({
  originName: z.string(),
  destinationName: z.string(),
  departureAt: isoDateSchema,
  proposedCost: z.number(),
  currency: z.string(),
  /** Ride lifecycle status — lets the UI gate "I've arrived" until the driver
   *  has actually started the trip (ride === 'departed'). */
  status: rideStatusSchema,
});
export type BookingRideSummary = z.infer<typeof bookingRideSummarySchema>;

export const bookingDtoSchema = z.object({
  id: uuidLikeSchema,
  rideId: uuidLikeSchema,
  ride: bookingRideSummarySchema.nullable(),
  passenger: publicUserSchema,
  seats: z.number().int(),
  status: bookingStatusSchema,
  messageToDriver: z.string().nullable(),
  luggage: z.enum(['small', 'medium', 'large']).nullable(),
  companions: z.array(z.string()).nullable(),
  /** Partial-trip boarding/alighting stops. Null = full trip (origin → dest). */
  fromCity: citySchema.nullable(),
  toCity: citySchema.nullable(),
  /** Price for this booking's sub-trip (= ride price when full trip). */
  segmentPrice: z.number(),
  confirmedAt: isoDateSchema.nullable(),
  createdAt: isoDateSchema,
  approvedAt: isoDateSchema.nullable(),
  cancelledAt: isoDateSchema.nullable(),
  completedAt: isoDateSchema.nullable(),
  ratedByDriver: z.boolean(),
  ratedByPassenger: z.boolean(),
});
export type BookingDto = z.infer<typeof bookingDtoSchema>;

// ---------------------------------------------------------------------------
// Ratings + reports
// ---------------------------------------------------------------------------
export const rateBookingRequestSchema = z.object({
  stars: z.number().int().min(1).max(5),
  comment: z.string().max(280).optional(),
  tags: z.array(z.string().max(40)).max(10).optional(),
});
export type RateBookingRequest = z.infer<typeof rateBookingRequestSchema>;

export const reportReasonSchema = z.enum([
  'no_show',
  'dangerous_driving',
  'overcharging',
  'commercial_activity',
  'harassment',
  'spam',
  'other',
]);

export const createReportRequestSchema = z.object({
  reportedUserId: uuidLikeSchema.optional(),
  rideId: uuidLikeSchema.optional(),
  bookingId: uuidLikeSchema.optional(),
  reason: reportReasonSchema,
  description: z.string().max(1000).optional(),
});
export type CreateReportRequest = z.infer<typeof createReportRequestSchema>;

// ---------------------------------------------------------------------------
// Premium
// ---------------------------------------------------------------------------
export const premiumInvoiceRequestSchema = z.object({
  plan: z.enum(['monthly']).optional(),
});
export type PremiumInvoiceRequest = z.infer<typeof premiumInvoiceRequestSchema>;

// ---------------------------------------------------------------------------
// API error envelope
// ---------------------------------------------------------------------------
export const apiErrorSchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
    details: z.record(z.unknown()).optional(),
  }),
});
export type ApiErrorSchema = z.infer<typeof apiErrorSchema>;

// ---------------------------------------------------------------------------
// Leaderboard (V1 scaffold — global "top drivers")
// ---------------------------------------------------------------------------
export const leaderboardEntrySchema = z.object({
  rank: z.number().int(),
  userId: uuidLikeSchema,
  displayName: z.string(),
  avatarUrl: z.string().url().nullable(),
  ridesCompleted: z.number().int(),
  ratingAvg: z.number().nullable(),
  tierLabelKey: z.string(),
});
export type LeaderboardEntry = z.infer<typeof leaderboardEntrySchema>;

export const leaderboardResponseSchema = z.object({
  items: z.array(leaderboardEntrySchema),
});
export type LeaderboardResponse = z.infer<typeof leaderboardResponseSchema>;

// ---------------------------------------------------------------------------
// Route subscriptions (passenger "follow this route" alerts)
// ---------------------------------------------------------------------------
export const createSubscriptionRequestSchema = z
  .object({
    originCityId: uuidLikeSchema,
    destinationCityId: uuidLikeSchema,
    maxPrice: z.number().positive().max(1_000_000).optional(),
    womenOnly: z.boolean().optional(),
  })
  .refine((v) => v.originCityId !== v.destinationCityId, {
    message: 'Origin and destination must differ',
  });
export type CreateSubscriptionRequest = z.infer<typeof createSubscriptionRequestSchema>;

export const subscriptionDtoSchema = z.object({
  id: uuidLikeSchema,
  origin: citySchema,
  destination: citySchema,
  maxPrice: z.number().nullable(),
  womenOnly: z.boolean(),
  active: z.boolean(),
});
export type SubscriptionDto = z.infer<typeof subscriptionDtoSchema>;

export const subscriptionListResponseSchema = z.object({
  items: z.array(subscriptionDtoSchema),
});
export type SubscriptionListResponse = z.infer<typeof subscriptionListResponseSchema>;

// ---------------------------------------------------------------------------
// Safety share links (Phase C) — public, minimal trip view for family/friends.
// ---------------------------------------------------------------------------
export const createShareResponseSchema = z.object({
  token: z.string(),
  url: z.string(),
});
export type CreateShareResponse = z.infer<typeof createShareResponseSchema>;

/** Public, no-PII trip view returned to anyone holding the share token. */
export const sharedTripSchema = z.object({
  passengerFirstName: z.string(),
  driverFirstName: z.string(),
  originName: z.string(),
  destinationName: z.string(),
  departureAt: isoDateSchema,
  /** Ride lifecycle status (published/full/departed/completed/...). */
  status: z.string(),
  pickupTitle: z.string().nullable(),
  /** Whether the driver has confirmed departure (calm reassurance signal). */
  driverConfirmed: z.boolean(),
  expiresAt: isoDateSchema,
});
export type SharedTrip = z.infer<typeof sharedTripSchema>;
